import React from "react";
import { Box } from "@mui/material";
import { useAppSelector } from "../../../app/hooks";
import { selectBlobsByResourceId } from "../attachementsSlice";

interface PrintPreviewDocumentAttachmentsProps {
    /** Resource whose attachments are previewed — the document root guid or a selected child (requirement/reference). */
    resourceId: string;
    lang?: string;
}

const T = (langRaw?: string | null) => {
    const v = langRaw?.toLowerCase();

    if (v === "pl") {
        return {
            lp: "Lp.",
            name: "Nazwa załącznika",
            noRows: "Brak załączników do wyświetlenia.",
        };
    }

    return {
        lp: "No.",
        name: "Attachment name",
        noRows: "No attachments to display.",
    };
};


export const PrintPreviewDocumentAttachments: React.FC<
    PrintPreviewDocumentAttachmentsProps
> = ({ resourceId, lang = "pl" }) => {
    const t = T(lang);

    const { blobs } =
        useAppSelector(
            (state) =>
                selectBlobsByResourceId(state, resourceId) ?? {
                    resourceId,
                    blobs: [],
                }
        );

    const hasBlobs = (blobs?.length ?? 0) > 0;

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                p: 2,
                border: "1px solid #ddd",
                fontFamily: `Arial, "Helvetica Neue", Helvetica, sans-serif`,
                lineHeight: 1.6,
                color: "#000",
                "& h1": { fontSize: "1.25rem", margin: "0 0 8px 0" },
                "& section": { pageBreakInside: "avoid" },
                "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "16px",
                    pageBreakInside: "avoid",
                },
                "& th, & td": {
                    border: "1px solid #000",
                    padding: "6px 8px",
                    verticalAlign: "top",
                    fontSize: "0.9rem",
                    wordBreak: "break-word",
                },
                "& th": {
                    backgroundColor: "#f0f0f0",
                    fontWeight: 700,
                    textAlign: "left",
                },
                "@media print": { border: "none", p: 0 },
            }}
        >
            <section>
                <table aria-label="document-attachments">
                    <thead>
                        <tr>
                            <th style={{ width: 45, textAlign: "center", }}>{t.lp}</th>
                            <th>{t.name}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {hasBlobs ? (
                            blobs!.map((b, idx) => (
                                <tr key={b.blobId ?? idx}>
                                    {/* The persisted number, so the preview matches what the PDF prints
                                        and stays put when a file gets a new version. Falls back to the
                                        row position for files that predate the numbering. */}
                                    <td style={{ width: 45, textAlign: "right" }}>{b.sequenceNumber ?? idx + 1}</td>
                                    <td>{b.fileMeta.fileName}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan={2}
                                    style={{
                                        fontStyle: "italic",
                                        color: "#777",
                                        textAlign: "center",
                                    }}
                                >
                                    {t.noRows}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </section>
        </Box>
    );
};
